import { A2uiMessage } from '@a2ui/web_core/v0_9';
import {
  DeepsearchMessagePart,
  useCreateSession,
  useGetSession,
  useListSessions,
  useRunAgentSse,
} from '@ltvco/refresh-lib/hooks';
import { useSession } from '@ltvco/refresh-lib/v1';
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  fileToBase64,
  getFileKind,
  getUploadFileValidation,
  inferMimeFromExtension,
  isImageFile,
} from '../components/FileUpload';
import {
  AgentEvent,
  DeepSearchConversation,
  DeepSearchMessage,
  DeepSearchMessageAttachment,
} from '../types';
import { convertSessionToConversation } from '../utils/convertSessionToConversation';
import { buildAssistantMessages, parseA2uiString } from '../utils/utils';

const APP_NAME = 'deepsearch_agent';

interface DeepSearchContextType {
  conversations: DeepSearchConversation[];
  activeConversationId: string | null;
  activeMessages: DeepSearchMessage[];
  isLoading: boolean;
  isLoadingHistory: boolean;
  isErrorHistory: boolean;
  isLoadingSession: boolean;
  thinkingStep: string;
  sendMessage: (content: string, displayContent?: string, file?: File) => void;
  startNewConversation: () => void;
  selectConversation: (conversationId: string) => void;
}

export const DeepSearchContext = createContext<DeepSearchContextType>({
  conversations: [],
  activeConversationId: null,
  activeMessages: [],
  isLoading: false,
  isLoadingHistory: false,
  isErrorHistory: false,
  isLoadingSession: false,
  thinkingStep: '',
  sendMessage: () => {},
  startNewConversation: () => {},
  selectConversation: () => {},
});

export const DeepSearchProvider = ({ children }: { children: ReactNode }) => {
  const { session } = useSession();
  const userId = session.account?.account.user_info?.user_code ?? '';
  const createSessionMutation = useCreateSession();
  const runAgentSseMutation = useRunAgentSse();
  const {
    data: sessionsData,
    isLoading: isLoadingHistory,
    isError: isErrorHistory,
  } = useListSessions({ appName: APP_NAME, userId }, Boolean(userId));

  const [conversations, setConversations] = useState<DeepSearchConversation[]>(
    []
  );
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [sessionToLoad, setSessionToLoad] = useState<string | null>(null);
  const apiSessionIds = useRef<Set<string>>(new Set());
  const conversationsRef = useRef<DeepSearchConversation[]>([]);
  conversationsRef.current = conversations;

  useEffect(() => {
    if (!sessionsData) {
      return;
    }
    const apiConversations = sessionsData.map(convertSessionToConversation);
    apiSessionIds.current = new Set(apiConversations.map((c) => c.id));
    setConversations((prev) => {
      const prevIds = new Set(prev.map((c) => c.id));
      const newOnly = apiConversations.filter((c) => !prevIds.has(c.id));
      return [...newOnly, ...prev].sort(
        (a, b) =>
          (b.lastUpdateTime ?? Infinity) - (a.lastUpdateTime ?? Infinity)
      );
    });
  }, [sessionsData]);

  const { data: fullSessionData } = useGetSession(
    { appName: APP_NAME, userId, sessionId: sessionToLoad ?? '' },
    Boolean(sessionToLoad) && Boolean(userId)
  );

  useEffect(() => {
    if (!fullSessionData || !sessionToLoad) {
      return;
    }
    const fullConversation = convertSessionToConversation(fullSessionData);
    setConversations((prev) =>
      prev.map((c) => (c.id === sessionToLoad ? fullConversation : c))
    );
    setSessionToLoad(null);
  }, [fullSessionData, sessionToLoad]);

  const [isLoading, setIsLoading] = useState(false);
  const [thinkingStep, setThinkingStep] = useState('');

  const activeMessages =
    conversations.find((c) => c.id === activeConversationId)?.messages ?? [];

  const addMessageToConversation = useCallback(
    (conversationId: string, message: DeepSearchMessage) => {
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId
            ? { ...c, messages: [...c.messages, message] }
            : c
        )
      );
    },
    []
  );

  const startNewConversation = useCallback(() => {
    setActiveConversationId(null);
  }, []);

  const selectConversation = useCallback((conversationId: string) => {
    const exists = conversationsRef.current.some(
      (c) => c.id === conversationId
    );
    if (exists) {
      setActiveConversationId(conversationId);
      if (apiSessionIds.current.has(conversationId)) {
        setSessionToLoad(conversationId);
      }
    }
  }, []);

  const sendMessage = useCallback(
    async (content: string, displayContent?: string, file?: File) => {
      const trimmedContent = content.trim();
      if ((!trimmedContent && !file) || isLoading || !userId) {
        return;
      }

      if (file) {
        // Defense-in-depth: reject invalid uploads here even if the UI layer
        // already validated, so callers can't send mislabeled/unsupported
        // files or bypass the size cap.
        const { isValidFile, isValidSize } = getUploadFileValidation(file);
        if (!isValidFile || !isValidSize) {
          return;
        }
      }

      setIsLoading(true);

      let conversationId = activeConversationId;
      const streamingMsgId = crypto.randomUUID();

      try {
        // Convert the attached file to base64 + build the inline_data part
        // BEFORE we touch session state — if base64 fails, nothing changes.
        let inlineDataPart: DeepsearchMessagePart | null = null;
        let attachment: DeepSearchMessageAttachment | undefined;
        if (file) {
          const base64 = await fileToBase64(file);
          const mimeType = inferMimeFromExtension(file);
          inlineDataPart = {
            inlineData: { mimeType, data: base64 },
          };
          attachment = {
            // Use an object URL instead of embedding the full base64 data URL
            // in state — a 10 MB image would otherwise retain ~13 MB as a
            // string for the life of the conversation.
            previewUrl: isImageFile(file) ? URL.createObjectURL(file) : '',
            mimeType,
            fileName: file.name,
            kind: getFileKind(file),
          };
        }

        if (!conversationId) {
          const session = await createSessionMutation.mutateAsync({
            params: { appName: APP_NAME, userId },
          });
          apiSessionIds.current.add(session.id);
          const optimisticTitle = trimmedContent || file?.name || 'New chat';
          setConversations((prev) => [
            {
              id: session.id,
              title: optimisticTitle,
              isNew: true,
              lastUpdateTime: Date.now(),
              messages: [],
            },
            ...prev,
          ]);
          setActiveConversationId(session.id);
          conversationId = session.id;
        }

        const activeId = conversationId!;

        addMessageToConversation(activeId, {
          id: crypto.randomUUID(),
          data: (displayContent ?? content).trim(),
          role: 'user',
          attachment,
        });

        const parts: DeepsearchMessagePart[] = [];
        if (inlineDataPart) {
          parts.push(inlineDataPart);
        }
        if (trimmedContent) {
          parts.push({ text: trimmedContent });
        }

        const response = await runAgentSseMutation.mutateAsync({
          appName: APP_NAME,
          userId,
          sessionId: activeId,
          newMessage: {
            role: 'user',
            parts,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let lastText = '';
        let lastA2ui: A2uiMessage[] = [];

        const processEvent = (event: AgentEvent) => {
          const stepFromDelta = event.actions?.stateDelta?.['thinking_step'];
          if (typeof stepFromDelta === 'string' && stepFromDelta) {
            setThinkingStep(stepFromDelta);
          }

          // Extract a2ui from functionResponse parts emitted by render tools.
          for (const part of event?.content?.parts ?? []) {
            const a2uiStr = part.functionResponse?.response?.a2ui;
            if (typeof a2uiStr === 'string') {
              const parsed = parseA2uiString(a2uiStr);
              if (parsed.length > 0) {
                lastA2ui = parsed;
              }
            }
          }

          const text = event?.content?.parts
            ?.filter((p) => p.text)
            .map((p) => p.text ?? '')
            .join('');
          if (text) {
            lastText = text;
            setConversations((prev) =>
              prev.map((c) =>
                c.id === activeId
                  ? {
                      ...c,
                      messages: c.messages.map((m) =>
                        m.id === streamingMsgId ? { ...m, data: text } : m
                      ),
                    }
                  : c
              )
            );
          }

          const titleFromDelta =
            event.actions?.stateDelta?.['volt:session_title'];
          const normalizedTitle =
            typeof titleFromDelta === 'string' ? titleFromDelta.trim() : '';
          if (normalizedTitle.length > 0) {
            setConversations((prev) =>
              prev.map((c) =>
                c.id === activeId ? { ...c, title: normalizedTitle } : c
              )
            );
          }
        };

        let chunk = await reader.read();
        while (!chunk.done) {
          buffer += decoder.decode(chunk.value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data:')) {
              continue;
            }
            const raw = line.slice(5).trimStart();
            if (!raw || raw === '[DONE]') {
              continue;
            }

            try {
              processEvent(JSON.parse(raw) as AgentEvent);
            } catch {
              // skip invalid JSON
            }
          }

          chunk = await reader.read();
        }

        // Flush decoder and process any unterminated final line
        buffer += decoder.decode();
        if (buffer.startsWith('data:')) {
          const raw = buffer.slice(5).trimStart();
          if (raw && raw !== '[DONE]') {
            try {
              processEvent(JSON.parse(raw) as AgentEvent);
            } catch {
              // skip invalid JSON
            }
          }
        }

        const finalMessages = lastText
          ? buildAssistantMessages(
              lastText,
              lastA2ui.length > 0 ? lastA2ui : undefined
            )
          : [
              {
                id: crypto.randomUUID(),
                data: 'Something went wrong. Please try again.',
                role: 'assistant' as const,
              },
            ];

        setConversations((prev) =>
          prev.map((c) =>
            c.id === activeId
              ? {
                  ...c,
                  messages: [
                    ...c.messages.filter((m) => m.id !== streamingMsgId),
                    ...finalMessages,
                  ],
                }
              : c
          )
        );
      } catch {
        setConversations((prev) =>
          prev.map((c) => {
            if (c.id !== conversationId) {
              return c;
            }
            return {
              ...c,
              messages: [
                ...c.messages.filter((m) => m.id !== streamingMsgId),
                {
                  id: crypto.randomUUID(),
                  data: 'Something went wrong. Please try again.',
                  role: 'assistant' as const,
                },
              ],
            };
          })
        );
      } finally {
        setIsLoading(false);
        setThinkingStep('');
      }
    },
    [
      isLoading,
      activeConversationId,
      userId,
      createSessionMutation,
      runAgentSseMutation,
      addMessageToConversation,
    ]
  );

  return (
    <DeepSearchContext.Provider
      value={{
        conversations,
        activeConversationId,
        activeMessages,
        isLoading,
        isLoadingHistory,
        isErrorHistory,
        isLoadingSession: Boolean(sessionToLoad),
        thinkingStep,
        sendMessage,
        startNewConversation,
        selectConversation,
      }}
    >
      {children}
    </DeepSearchContext.Provider>
  );
};
